import { ref, readonly, onUnmounted, getCurrentInstance } from 'vue';

// --- Singleton state (module-level, shared across all consumers) ---
const pullDistance = ref(0);
const isPulling = ref(false);
const isRefreshing = ref(false);

const THRESHOLD = 70;
const MAX_PULL = 120;
const RESISTANCE = 0.4;
const MOBILE_MAX_WIDTH = 768;

const EXCLUDED_SELECTORS = ['.bottom-sheet', '.v-dialog', '.v-overlay'];

let refreshCallback: (() => Promise<void>) | null = null;
let startY = 0;
let listening = false;

function isExcluded(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false;
  return EXCLUDED_SELECTORS.some(sel => target.closest(sel) !== null);
}

function onTouchStart(e: TouchEvent) {
  if (isRefreshing.value) return;
  if (window.innerWidth > MOBILE_MAX_WIDTH) return;
  if (window.scrollY > 0) return;
  if (isExcluded(e.target)) return;

  startY = e.touches[0].clientY;
  isPulling.value = false;
  pullDistance.value = 0;
}

function onTouchMove(e: TouchEvent) {
  if (isRefreshing.value) return;
  if (window.innerWidth > MOBILE_MAX_WIDTH) return;
  if (window.scrollY > 0) {
    // User scrolled down mid-gesture — reset
    if (isPulling.value) {
      isPulling.value = false;
      pullDistance.value = 0;
    }
    return;
  }
  if (isExcluded(e.target)) return;

  const currentY = e.touches[0].clientY;
  const delta = currentY - startY;

  if (delta <= 0) {
    if (isPulling.value) {
      isPulling.value = false;
      pullDistance.value = 0;
    }
    return;
  }

  // We're pulling down from top
  isPulling.value = true;
  e.preventDefault();

  // Rubber-band: linear up to threshold, then resistance
  let distance: number;
  if (delta <= THRESHOLD) {
    distance = delta;
  } else {
    distance = THRESHOLD + (delta - THRESHOLD) * RESISTANCE;
  }
  pullDistance.value = Math.min(distance, MAX_PULL);
}

async function onTouchEnd() {
  if (!isPulling.value) return;

  if (pullDistance.value >= THRESHOLD && refreshCallback) {
    isRefreshing.value = true;
    isPulling.value = false;
    pullDistance.value = THRESHOLD; // Hold at threshold during refresh

    try {
      await refreshCallback();
    } catch (e) {
      console.error('[PullToRefresh] callback error', e);
    } finally {
      isRefreshing.value = false;
      pullDistance.value = 0;
    }
  } else {
    // Snap back
    isPulling.value = false;
    pullDistance.value = 0;
  }
}

export function initPullToRefresh() {
  if (listening) return;
  document.addEventListener('touchstart', onTouchStart, { passive: true });
  document.addEventListener('touchmove', onTouchMove, { passive: false });
  document.addEventListener('touchend', onTouchEnd, { passive: true });
  listening = true;
}

export function usePullToRefresh(callback?: () => Promise<void>) {
  if (callback) {
    refreshCallback = callback;

    // Auto-cleanup when the component unmounts
    if (getCurrentInstance()) {
      onUnmounted(() => {
        if (refreshCallback === callback) {
          refreshCallback = null;
        }
      });
    }
  }

  return {
    pullDistance: readonly(pullDistance),
    isPulling: readonly(isPulling),
    isRefreshing: readonly(isRefreshing),
  };
}
